import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import translate from "../../../i18n/translate";
import { intl } from "../../../i18n/provider";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";

// Azy has two integrations with separate parameters that must never be
// mixed up: the Topup API, which every sale is posted to, and the Update
// products API, which the product update signs in to. Each has its own
// section, its own fields and its own save. Passwords are posted in the
// request body and never shown back; a blank field on save keeps the
// stored value.
const EMPTY = {
  topup: { url: "", agent_id: "" },
  products: { url: "", user: "", password_set: false, agent_id: "", imported: false },
  balance: "",
};

const AzyAccounts = ({ isDarktheme }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(false);
  const [stored, setStored] = useState(EMPTY);
  const [form, setForm] = useState({
    topup: { url: "", agent_id: "" },
    products: { url: "", user: "", password: "", agent_id: "" },
  });
  const [showPassword, setShowPassword] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const applyStored = (data) => {
    const next = {
      topup: { url: data.topup?.url || "", agent_id: data.topup?.agent_id || "" },
      products: {
        url: data.products?.url || "",
        user: data.products?.user || "",
        password_set: Boolean(data.products?.password_set),
        agent_id: data.products?.agent_id || "",
        imported: Boolean(data.products?.imported),
      },
      balance: data.balance || "",
    };
    setStored(next);
    setForm({
      topup: { url: next.topup.url, agent_id: next.topup.agent_id },
      products: { url: next.products.url, user: next.products.user, password: "", agent_id: next.products.agent_id },
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiRequest.post("azy_accounts?operation=get");
      if (res?.data?.status === "success") applyStored(res.data);
    } catch (err) {
      // the axios interceptor already reports the failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const edit = (group, key, value) =>
    setForm((f) => ({ ...f, [group]: { ...f[group], [key]: value } }));

  // Only what differs from the stored value is sent.
  const pending = (group) => {
    const body = {};
    Object.keys(form[group]).forEach((key) => {
      const value = form[group][key].trim();
      if (key === "password") {
        if (value) body.password = value;
      } else if (value !== (stored[group][key] || "")) {
        body[key] = value;
      }
    });
    return body;
  };

  const save = async (group) => {
    const body = pending(group);
    if (!Object.keys(body).length) return;
    setSaving(group);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("azy_accounts?operation=update", { [group]: body });
      if (res?.data?.status === "success") {
        applyStored(res.data);
        setShowPassword(false);
        Toast.fire({ title: translate("azyAccountsSaved"), icon: "success" });
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSaving(null);
    }
  };

  const testProducts = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("azy_accounts?operation=test_products", {}, { timeout: 120000 });
      const ok = res?.data?.status === "success";
      setTestResult({ ok, reason: res?.data?.reason });
      if (ok && res.data.balance) setStored((s) => ({ ...s, balance: res.data.balance }));
    } catch (err) {
      setTestResult({ ok: false, reason: "Something Went Wrong!" });
    } finally {
      setTesting(false);
    }
  };

  const field = (group, key, label, extra = {}) => (
    <TextField
      size="small"
      fullWidth
      value={form[group][key]}
      onChange={(e) => edit(group, key, e.target.value)}
      label={label}
      autoComplete="off"
      {...extra}
    />
  );

  const saveButton = (group) => (
    <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
      <Button
        variant="contained"
        disabled={loading || saving === group || !Object.keys(pending(group)).length}
        onClick={() => save(group)}
      >
        {saving === group ? <CircularProgress size={20} color="inherit" /> : translate("Save")}
      </Button>
    </Box>
  );

  const status = (isSet) =>
    loading ? (
      <CircularProgress size={18} />
    ) : isSet ? (
      <Chip size="small" color="success" label={translate("set")} />
    ) : (
      <Chip size="small" color="warning" label={translate("not set")} />
    );

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("azyAccountsHint")}
      </Typography>

      <Paper sx={{ padding: 3, maxWidth: 620, marginBottom: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 0.5 }}>
          <Typography variant="subtitle1">{translate("Topup API")}</Typography>
          {status(Boolean(stored.topup.agent_id))}
        </Box>
        <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
          {translate("azyTopupHint")}
        </Typography>
        <Box sx={{ display: "grid", gap: 2 }}>
          {field("topup", "url", translate("API url"), { placeholder: "https://..." })}
          {field("topup", "agent_id", translate("Agent ID"))}
        </Box>
        {saveButton("topup")}
      </Paper>

      <Paper sx={{ padding: 3, maxWidth: 620, marginBottom: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 0.5 }}>
          <Typography variant="subtitle1">{translate("Update products API")}</Typography>
          {status(Boolean(stored.products.user) && stored.products.password_set)}
        </Box>
        <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
          {translate("azyProductsHint")}
        </Typography>
        {stored.products.imported && (
          <Alert severity="info" sx={{ marginBottom: 2 }}>
            {translate("azyPortalImported")}
          </Alert>
        )}
        <Box sx={{ display: "grid", gap: 2 }}>
          {field("products", "url", translate("API url"), { placeholder: "https://..." })}
          {field("products", "user", translate("User name"))}
          {field("products", "password", translate("Password"), {
            type: showPassword ? "text" : "password",
            placeholder: stored.products.password_set
              ? "••••••••  (" + intl.formatMessage({ id: "leave blank to keep" }) + ")"
              : "",
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end" aria-label="toggle password visibility">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          })}
          {field("products", "agent_id", translate("Agent ID"), {
            helperText: translate("optional"),
          })}
        </Box>
        {saveButton("products")}

        <Box sx={{ borderTop: 1, borderColor: "divider", marginTop: 3, paddingTop: 2 }}>
          <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
            {translate("azyProductsTestHint")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={testProducts}
              disabled={testing || loading || !stored.products.password_set || !stored.products.user}
            >
              {testing ? <CircularProgress size={20} color="inherit" /> : translate("Test login")}
            </Button>
            {stored.balance && (
              <Typography variant="body2">
                {translate("Balance")}: <strong>{stored.balance}</strong>
              </Typography>
            )}
          </Box>
          {testResult && (
            <Alert severity={testResult.ok ? "success" : "error"} sx={{ marginTop: 2 }}>
              {testResult.reason}
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(AzyAccounts);
